import { alpha } from '@mui/material';

const primaryColor = '#1976d2';
const primaryHover = '#1565c0';

export const styles = {
  container: {
    position: 'relative',
    maxWidth: '1200px',
    margin: '0 auto',
    p: { xs: 2, md: 3 }
  },

  header: {
    mb: 4,
    textAlign: 'center',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-16px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '60px',
      height: '3px',
      background: `linear-gradient(90deg, ${primaryColor}, ${alpha(primaryColor, 0.3)})`,
      borderRadius: '2px'
    }
  },

  iconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 64,
    height: 64,
    borderRadius: '16px',
    background: `linear-gradient(135deg, ${alpha(primaryColor, 0.1)} 0%, ${alpha(primaryColor, 0.05)} 100%)`,
    mb: 2,
    boxShadow: `0 4px 12px ${alpha(primaryColor, 0.15)}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 20px ${alpha(primaryColor, 0.2)}`
    }
  },

  icon: {
    fontSize: 32,
    color: primaryColor
  },

  title: {
    color: '#1e293b',
    fontWeight: 700,
    fontSize: '1.75rem',
    mb: 1,
    letterSpacing: '-0.02em'
  },

  subtitle: {
    color: '#64748b',
    fontSize: '0.95rem',
    fontWeight: 500,
    maxWidth: '600px',
    margin: '0 auto'
  },

  infoAlert: {
    mb: 3,
    background: `linear-gradient(135deg, ${alpha('#3b82f6', 0.08)} 0%, ${alpha('#3b82f6', 0.04)} 100%)`,
    border: `1px solid ${alpha('#3b82f6', 0.2)}`,
    borderRadius: 3,
    boxShadow: `0 2px 8px ${alpha('#3b82f6', 0.08)}`,
    '& .MuiAlert-message': {
      width: '100%'
    }
  },

  infoText: {
    color: '#1e40af',
    fontWeight: 600,
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },

  section: {
    p: 3,
    mb: 3,
    background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
    borderRadius: 3,
    border: `1px solid ${alpha('#000000', 0.06)}`,
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
      borderColor: alpha(primaryColor, 0.15)
    }
  },

  sectionTitle: {
    fontWeight: 700,
    color: '#1e293b',
    mb: 2.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    fontSize: '1.05rem',
    pb: 1.5,
    borderBottom: `2px solid ${alpha('#000000', 0.06)}`
  },

  sectionIcon: {
    color: primaryColor,
    fontSize: 22,
    padding: '4px',
    backgroundColor: alpha(primaryColor, 0.1),
    borderRadius: 1
  },

  input: {
    backgroundColor: '#ffffff',
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      transition: 'all 0.2s ease',
      '&:hover': {
        boxShadow: `0 2px 8px ${alpha(primaryColor, 0.08)}`
      },
      '&:hover fieldset': {
        borderColor: primaryColor
      },
      '&.Mui-focused': {
        boxShadow: `0 4px 12px ${alpha(primaryColor, 0.15)}`
      },
      '&.Mui-focused fieldset': {
        borderColor: primaryColor,
        borderWidth: '2px'
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: primaryColor,
      fontWeight: 600
    }
  },

  inputIcon: {
    color: '#94a3b8',
    fontSize: 20
  },

  uploadBox: {
    border: `2px dashed ${alpha(primaryColor, 0.3)}`,
    borderRadius: 3,
    p: 4,
    textAlign: 'center',
    background: `linear-gradient(135deg, ${alpha(primaryColor, 0.02)} 0%, ${alpha(primaryColor, 0.05)} 100%)`,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: primaryColor,
      background: `linear-gradient(135deg, ${alpha(primaryColor, 0.08)} 0%, ${alpha(primaryColor, 0.12)} 100%)`,
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 12px ${alpha(primaryColor, 0.15)}`
    }
  },

  filePreview: {
    display: 'flex',
    alignItems: 'center',
    p: 2.5,
    backgroundColor: '#ffffff',
    border: `1px solid ${alpha('#000000', 0.08)}`,
    borderRadius: 2.5,
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: alpha(primaryColor, 0.3),
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
    }
  },

  addButton: {
    color: primaryColor,
    borderColor: alpha(primaryColor, 0.3),
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 600,
    px: 2.5,
    py: 1,
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: primaryColor,
      backgroundColor: alpha(primaryColor, 0.08),
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 12px ${alpha(primaryColor, 0.2)}`
    }
  },

  actions: {
    display: 'flex',
    gap: 2,
    justifyContent: 'flex-end',
    mt: 4,
    pt: 3,
    borderTop: `2px solid ${alpha('#000000', 0.06)}`
  },

  cancelButton: {
    px: 3.5,
    py: 1.2,
    color: '#64748b',
    borderColor: alpha('#000000', 0.15),
    borderRadius: 2.5,
    textTransform: 'none',
    fontWeight: 600,
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: alpha('#000000', 0.25),
      backgroundColor: alpha('#000000', 0.05),
      transform: 'translateY(-1px)'
    }
  },

  submitButton: {
    px: 3.5,
    py: 1.2,
    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryHover} 100%)`,
    borderRadius: 2.5,
    textTransform: 'none',
    fontWeight: 700,
    boxShadow: `0 4px 12px ${alpha(primaryColor, 0.3)}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      background: `linear-gradient(135deg, ${primaryHover} 0%, #0d47a1 100%)`,
      boxShadow: `0 6px 20px ${alpha(primaryColor, 0.4)}`,
      transform: 'translateY(-2px)'
    },
    '&:disabled': {
      background: '#e2e8f0',
      color: '#94a3b8',
      boxShadow: 'none'
    }
  },

  tableContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 2,
    border: `1px solid ${alpha('#000000', 0.06)}`,
    overflow: 'hidden',
    '& .MuiTableCell-root': {
      borderColor: alpha('#000000', 0.06)
    },
    '& .MuiTableHead-root': {
      backgroundColor: alpha(primaryColor, 0.04),
      '& .MuiTableCell-root': {
        fontWeight: 700,
        color: '#1e293b',
        fontSize: '0.85rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      }
    }
  },

  deleteIconButton: {
    color: '#94a3b8',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: alpha('#ef4444', 0.12),
      color: '#ef4444',
      transform: 'scale(1.1)'
    }
  }
};